import { errorMessage } from '@/api/client';
import { useIncidents } from '@/api/queries';
import { Button } from '@/components/Button';
import { Field, Input, RadioGroup, Select, Textarea } from '@/components/fields';
import { ErrorNotice, Notice } from '@/components/primitives';
import type { Incident, Scope } from '@/domain/types';

import type { DecisionAction, DecisionValues } from './decisionState';

/**
 * The incident decision (TDD §5.4): none, open a new incident, or attach to one already open
 * on the page. Shared by approve, redecide and manual proposals so the three read the same.
 */
export function DecisionFields({
  scope,
  values,
  onChange,
  errors,
  disabled = false,
}: {
  scope: Scope;
  values: DecisionValues;
  onChange: (values: DecisionValues) => void;
  errors: Record<string, string>;
  disabled?: boolean;
}) {
  const incidents = useIncidents(scope);
  const open: Incident[] = incidents.data?.incidents ?? [];
  // "None open" is a fact about the page only when the page was read (CHG-043): a failed or
  // pending read says so, so an operator is not invited to open a second incident.
  const unread = incidents.data === undefined;
  const failed = unread && incidents.isError;
  const attachHint = !unread
    ? open.length > 0
      ? `${open.length} open`
      : 'None open'
    : failed
      ? "Could not read the page's incidents"
      : 'Reading the page…';
  const set = (patch: Partial<DecisionValues>) => onChange({ ...values, ...patch });

  return (
    <div className="flex flex-col gap-4">
      <RadioGroup<DecisionAction>
        legend="On the status page"
        name="incident_action"
        value={values.incident_action}
        onChange={(incident_action) => set({ incident_action })}
        options={[
          { value: 'none', label: 'Status change only' },
          { value: 'open', label: 'Open a new incident' },
          {
            value: 'attach',
            label: 'Add to an open incident',
            hint: attachHint,
            disabled: open.length === 0,
          },
        ]}
      />

      {failed ? (
        <ErrorNotice
          title="Could not read the page's incidents"
          message={errorMessage(incidents.error)}
          action={
            <Button size="sm" onClick={() => void incidents.refetch()}>
              Try again
            </Button>
          }
        />
      ) : null}

      {values.incident_action === 'open' ? (
        <>
          <Field
            label="Incident title"
            required
            error={errors.incident_title}
            hint="Cannot be renamed later."
          >
            {(props) => (
              <Input
                {...props}
                name="incident_title"
                autoComplete="off"
                value={values.incident_title}
                onChange={(e) => set({ incident_title: e.target.value })}
                placeholder="e.g. CSV imports failing"
                disabled={disabled}
              />
            )}
          </Field>
          <Field label="First update" required error={errors.incident_body} hint="Customer-facing.">
            {(props) => (
              <Textarea
                {...props}
                name="incident_body"
                value={values.incident_body}
                onChange={(e) => set({ incident_body: e.target.value })}
                placeholder="e.g. CSV import jobs are not completing. We are investigating and will update as we learn more."
                disabled={disabled}
              />
            )}
          </Field>
        </>
      ) : null}

      {values.incident_action === 'attach' ? (
        <>
          <Field label="Incident" required error={errors.incident_title}>
            {(props) => (
              <Select
                {...props}
                name="incident_title"
                value={values.incident_title}
                onChange={(e) => set({ incident_title: e.target.value })}
                disabled={disabled}
              >
                <option value="">Choose an open incident…</option>
                {open.map((incident) => (
                  <option key={incident.id} value={incident.name}>
                    {incident.name} · {stageLabel(incident.status)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Update" required error={errors.incident_body}>
            {(props) => (
              <Textarea
                {...props}
                name="incident_body"
                value={values.incident_body}
                onChange={(e) => set({ incident_body: e.target.value })}
                placeholder="e.g. CEDE Import is also affected."
                disabled={disabled}
              />
            )}
          </Field>
        </>
      ) : null}
    </div>
  );
}

/** The name recorded on the decision, prefilled from preferences and saved back on submit. */
export function ApproverField({
  value,
  onChange,
  error,
  disabled,
  label = 'Decided by',
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string | undefined;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <Field label={label} required error={error}>
      {(props) => (
        <Input
          {...props}
          name="approver"
          autoComplete="name"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Your name…"
          disabled={disabled}
        />
      )}
    </Field>
  );
}

/** The message a request came back with; the form's own gap spaces it, so it adds none. */
export function ErrorSummary({ message }: { message: string | null }) {
  if (!message) return null;
  return <Notice tone="danger">{message}</Notice>;
}

/** Statuspage stage names are lowercase machine words; operators read them capitalised. */
function stageLabel(stage: string): string {
  return stage.charAt(0).toUpperCase() + stage.slice(1);
}
